import { useCallback, useEffect, useState } from "react"
import type { IMembershipPermissionNormalized } from "../../permissions/interfaces/permission-normalized"
import type { Translations } from "../hooks/use-settings-page-translations"
import type { AbilitiesInResources, Option, AbilitiesUpdateData } from "./types"
import { useSnackbar } from "notistack"
import { isNormalMember } from "../utils"
import doublet from "../../utils/doublet"
import { MembershipRoleEnum } from "../../types/memberships"
import { useConfirmDialog } from "../../components/ConfirmDialog"
import Typography from "@material-ui/core/Typography"
import AbilitiesTable from "../../components/AbilitiesTable"
import AssociationsManager from "./AssociationsManager"
import Divider from "@material-ui/core/Divider"
import Stack from "../../components/Stack"
import Button from "@material-ui/core/Button"
import MemberMenu from "./MemberMenu"
import type { MembershipViewModel } from "../api/membership"
import { useSettingsPageTranslations } from "../hooks/use-settings-page-translations"

type MembershipPermissionManagerProps = {
  membership: MembershipViewModel
  permission: IMembershipPermissionNormalized
  setAbilitiesInResources: (data: AbilitiesInResources) => any
  setAssociations: (data: string[]) => any
  updateAbilities: (data: AbilitiesUpdateData) => any
  copyPermissions: (copyFrom: string) => any
  customerOptions: Option[]
  supplierOptions: Option[]
  membershipOptions: Option[]
  translations: Translations
}

export const MembershipPermissionManager = ({
  membership,
  permission,
  copyPermissions,
  setAbilitiesInResources,
  setAssociations,
  updateAbilities,
  customerOptions,
  supplierOptions,
  membershipOptions,
  translations: baseTranslations,
}: MembershipPermissionManagerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const roleLabel = isNormalMember(membership)
    ? baseTranslations.standardMemberLabel
    : baseTranslations.limitedMemberLabel
  const { enqueueSnackbar } = useSnackbar()
  const [copyFromMemberId, setCopyFromMemberId] = useState<string | undefined>(undefined)
  const [confirmCopyFromSelectedMember, setConfirmCopyFromSelectedMember] = useState(false)
  const copyPermissionFromMemberName = getMemberById(membershipOptions, copyFromMemberId)?.name || ""
  const translations = useSettingsPageTranslations({
    name: membership.fullname,
    role: roleLabel,
    copyToName: membership.fullname,
    copyPermissionFromMemberName,
  })

  const basePermissionInfo = translations.currentBasePermissionInfo

  const onCopyFromStandardMember = async () => {
    const [error] = await doublet(copyPermissions, MembershipRoleEnum.orgMember)
    if (error) {
      const message = error.message || "Failed to copy permissions from standard member"
      return enqueueSnackbar(message, { variant: "error" })
    }
    return enqueueSnackbar(translations.resetPermissionToStandardMemberSuccess, { variant: "success" })
  }

  const onCopyFromLimitedMember = async () => {
    const [error] = await doublet(copyPermissions, MembershipRoleEnum.orgLimitedMember)
    if (error) {
      const message = error.message || "Failed to copy permissions from limited member"
      return enqueueSnackbar(message, { variant: "error" })
    }
    return enqueueSnackbar(translations.resetPermissionToLimitedMemberSuccess, { variant: "success" })
  }

  const onCopyFromMember = useCallback(
    async (copyFrom: string) => {
      setAnchorEl(null)
      const [error] = await doublet(copyPermissions, copyFrom)
      if (error) {
        const message = error.message || "Failed to copy permissions from member"
        return enqueueSnackbar(message, { variant: "error" })
      }
      const message = translations.copyPermissionFromMemberSuccess
      return enqueueSnackbar(message, { variant: "success" })
    },
    [copyPermissions, enqueueSnackbar, setAnchorEl, translations]
  )

  const { confirm: confirmCopyFromStandardMember } = useConfirmDialog({
    title: translations.resetToStandardMemberLabel,
    text: translations.resetPermissionToStandardConfirmationDialogText,
    onConfirm: () => onCopyFromStandardMember(),
  })

  const { confirm: confirmCopyFromLimitedMember } = useConfirmDialog({
    title: translations.resetToLimitedMemberLabel,
    text: translations.resetPermissionToLimitedConfirmationDialogText,
    onConfirm: () => onCopyFromLimitedMember(),
  })

  const { confirm: confirmCopyFromMember } = useConfirmDialog({
    title: translations.copyFromMemberLabel,
    text: translations.copyPermissionFromMemberConfirmationDialogText,
    onConfirm: () => setConfirmCopyFromSelectedMember(true),
    onCancel: () => setConfirmCopyFromSelectedMember(false),
  })

  useEffect(() => {
    if (confirmCopyFromSelectedMember) {
      onCopyFromMember(copyFromMemberId || "")
      setConfirmCopyFromSelectedMember(false)
    }
  }, [confirmCopyFromSelectedMember, copyFromMemberId, onCopyFromMember])

  return (
    <>
      <div style={{ marginTop: 16 }}>
        <Typography variant="h6" style={{ marginTop: 16, marginBottom: 8 }}>
          {translations.managePermissionsLabel}
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
          {basePermissionInfo}
        </Typography>
        <div style={{ marginLeft: -12, marginRight: -12, marginTop: 20 }}>
          {permission && (
            <AbilitiesTable groupedAbilities={permission.abilitiesByResources} onUpdateAbilities={updateAbilities} />
          )}
        </div>
        {membership.isLimitedMember && permission && (
          <>
            <div style={{ marginBottom: 20, marginTop: 20 }}>
              <AssociationsManager
                abilities={permission.abilities}
                abilitiesInResources={permission.abilitiesInResources}
                associations={permission.associations}
                setAbilitiesInResources={setAbilitiesInResources}
                setAssociations={setAssociations}
                updateAbilities={updateAbilities}
                customerOptions={customerOptions}
                supplierOptions={supplierOptions}
                canUpdatePermissions={membership.canUpdatePermissions}
              />
            </div>
            <Divider />
          </>
        )}

        <Typography variant="h4" gutterBottom style={{ margin: "16px 0" }}>
          {translations.resetButtonLabel}
        </Typography>

        {membership.canUpdatePermissions && (
          <Stack>
            <div>
              <Typography variant="subtitle1" gutterBottom>
                {translations.resetToStandardMemberLabel}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {translations.normalMembershipHelperText}
              </Typography>
              <Button
                size="small"
                style={{ marginLeft: -4, marginTop: 8 }}
                color="primary"
                onClick={confirmCopyFromStandardMember}
              >
                {translations.resetToStandardMemberLabel}
              </Button>
            </div>

            <Divider />

            <div>
              <Typography variant="subtitle1" gutterBottom>
                {translations.resetToLimitedMemberLabel}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {translations.limitedMembershipHelperText}
              </Typography>
              <Button
                size="small"
                style={{ marginLeft: -4, marginTop: 8 }}
                color="primary"
                onClick={confirmCopyFromLimitedMember}
              >
                {translations.resetToLimitedMemberLabel}
              </Button>
            </div>

            <Divider />

            <div>
              <Typography variant="subtitle1" gutterBottom>
                {translations.copyFromMemberLabel}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {translations.copiedMemberHelperText}
              </Typography>
              <Button
                size="small"
                style={{ marginLeft: -4, marginTop: 8 }}
                color="primary"
                onClick={(e) => setAnchorEl(e.currentTarget)}
              >
                {translations.copyFromMemberLabel}
              </Button>
              <MemberMenu
                anchor={anchorEl}
                menuTitle={translations.chooseMemberToCopyLabel}
                searchInputPlaceholder={translations.searchMembersPlaceholder}
                options={getMembersWithAccess(membershipOptions)}
                onClickAway={() => setAnchorEl(null)}
                onClickMenuItem={({ id }) => {
                  setCopyFromMemberId(id)
                  confirmCopyFromMember()
                }}
                PopperProps={{ placement: "bottom-start" }}
              />
            </div>
          </Stack>
        )}
      </div>
    </>
  )
}

const getMembersWithAccess = (options: Option[]) => {
  const filteredOptions = options.filter((option) => option.type !== "noAccessMember")
  return filteredOptions.sort((a, b) => a.name.localeCompare(b.name))
}

const getMemberById = (options: Option[], id?: string) => {
  return options.find((member) => member.id === id)
}
